import { SettingChange } from 'src/components/SettingsPropagationDialog';

/**
 * Categorizes changed fields by their target model (platform, user)
 * and formats them for display in the propagation dialog
 */
export interface CategorizedChanges {
  platform: Record<string, any>;
  user: Record<string, any>;
}

/**
 * Takes the raw changed fields from Formik and categorizes them by model
 */
export const categorizeChangedFields = (
  changedFields: Record<string, any>
): CategorizedChanges => {
  const result: CategorizedChanges = {
    platform: {},
    user: {},
  };

  Object.entries(changedFields).forEach(([path, value]) => {
    if (path.startsWith('platform.')) {
      // Remove the 'platform.' prefix and add to platform changes
      const fieldName = path.replace('platform.', '');
      result.platform[fieldName] = value;
    } else if (
      path.startsWith('whiteLabeler.') ||
      path.startsWith('trainer.')
    ) {
      // Remove the prefix and add to user changes
      const fieldName = path.replace(/^(whiteLabeler\.|trainer\.)/, '');
      result.user[fieldName] = value;
    } else {
      // Fallback for any other top-level fields
      result.platform[path] = value;
    }
  });

  return result;
};

/**
 * Map of field paths to their translation keys
 * This allows us to reuse existing translations from the settings pages
 * Now uses nested path structure to match the actual form structure
 */
const fieldTranslationMap: Record<string, string> = {
  // Platform Page fields (settings.platform prefix)
  'whiteLabeler.otpPlatform': 'requireTwoFactorAuthentication',
  'whiteLabeler.messagePlanPurchasers': 'messagePlanPurchasers',
  'platform.viewAllTrainerMessages': 'viewAllTrainerMessages',
  'platform.disableAllAccountEmails': 'disableAllAccountEmails',
  'platform.disableEmailExceptions': 'disableEmailExceptions',
  'platform.allowAccountsToMessageSelectedStaff':
    'allowAccountsToMessageSelectedStaff',
  'platform.disablePlatformSettingsTab': 'hidePlatformSettingsTab',
  'whiteLabeler.disabledTabs': 'disableLogging',
  'whiteLabeler.firstWorkoutEmail': 'firstWorkoutEmail',
  'platform.disableRepMax': 'disableRepMax',
  'platform.autoFillWorkoutWeights': 'autoFillWorkoutWeights',
  'platform.doubleEachSideWorkoutWeights': 'doubleEachSideWorkoutWeights',
  'whiteLabeler.metricPlatform': 'useMetricSystem',
  'platform.requireChargedBy': 'requireChargedBy',
  'platform.allowCoupons': 'allowCouponsForExistingSubscriptions',
  'platform.noCcForTrials': 'allowSubscriptionTrialsWithoutCreditCard',
  'platform.cancelTrialOnPaymentFailure': 'cancelTrialOnPaymentFailure',
  'platform.subscriptionInvoiceRetryBehavior':
    'subscriptionInvoiceRetryBehavior',
  'platform.allowUsersToSeeAccountBalance':
    'allowUsersToSeeAccountBalance',
  'platform.otherPaymentMethodLabels': 'customPaymentMethods',
  'platform.hideProfileStats': 'hideProfileStats',
  'platform.hideBlockTitles': 'hideBlockTitles',
  'whiteLabeler.showActivity': 'showActivity',
  'platform.hideMyPerformance': 'hideMyPerformance',
  'whiteLabeler.contactUrl': 'contactPage',
  'whiteLabeler.showTrainingOnThankYouPage': 'showTrainingOnThankYouPage',
  'whiteLabeler.showItemsOnThankYouPage': 'showItemsOnThankYouPage',
  'platform.thankYouGroups': 'thankYouGroups',
  'platform.thankYouPlans': 'thankYouPlans',
  'platform.hideLandingPage': 'hideLandingPage',
  'platform.hideClientLanding': 'hideClientLanding',
  'platform.landingGroups': 'landingGroups',
  'platform.landingWorkoutPlans': 'landingWorkoutPlans',
  'platform.landingPackages': 'landingPackages',
  'whiteLabeler.alternateLanding': 'alternateLandingPage',
  'platform.packageLandingTags': 'packageTagsToShowOnLanding',
  'whiteLabeler.useFinderFilters': 'useFinderFilters',
  'platform.publicExerciseTags': 'publicExerciseTags',
  'platform.freeSignups': 'allowFreeUserSignups',
  'platform.freeUserGroup': 'freeUserGroup',
  'whiteLabeler.customSignup': 'customSignupPage',
  'platform.darkModeForCheckout': 'darkModeForCheckout',
  'platform.useLongCheckout': 'useLongCheckout',
  'whiteLabeler.trainerProfileFields': 'trainerProfileFields',
  'platform.requireProfileForCheckout': 'requireProfileForCheckout',
  'whiteLabeler.requireWaiver': 'requireWaiver',
  'whiteLabeler.waiver': 'waiver',
  'platform.requiredWaiverAppText': 'requiredAppText',
  'platform.requireCustomTerms': 'requireCustomTerms',
  'platform.customTerms': 'customTerms',
  'platform.showOtherPlansOnIndexPage': 'showOtherPlansOnIndexPage',
  'platform.showCollectionScroller': 'showCollectionScroller',

  // Scheduling Settings fields (settings.schedulingSettings prefix)
  'platform.cancellationPolicyHours': 'cancelPolicy',
  'platform.doNotDeductLateCancel': 'doNotDeductLateCancel',
  'platform.excludeLateCancellationsFromLimitBooking': 'excludeLate',
  'platform.excludeNoShowsFromLimitBooking': 'excludeNoShows',
  'platform.disableLateCancellationRescheduling':
    'disableLateCancellationRescheduling',
  'platform.freeClasses': 'freeClasses',
  'platform.freeClassPackage': 'freeClassPackage',
  'platform.freeNoshowVisits': 'freeNoshowVisits',
  'platform.showScheduleTime': 'showScheduleTime',
  'platform.firstDayOfWeek': 'firstDayOfWeek',
  'platform.requireTrainerForBooking': 'requireTrainerForBooking',
  'platform.disableClientCheckin': 'disableClientCheckin',
  'platform.disableRescheduling': 'disableRescheduling',
  'platform.serviceBalanceNoPrepayment': 'serviceBalanceNoPrepayment',
  'platform.bookingCalendar': 'bookingCalendar',
  'platform.hideSpotsRemaining': 'hideSpotsRemaining',
  'platform.forceDependent': 'forceDependent',
  'platform.bookPackageSessions': 'bookPackageSessions',
  'platform.useUserLocation': 'useUserLocation',
  'platform.showFullSessions': 'showFullSessions',
  'platform.showClosedSessions': 'showClosedSessions',
  'platform.bookingAvailableDefault': 'bookingAvailableDefault',
  'platform.limitBooking': 'limitBooking',
  'platform.limitBookingDays': 'limitBookingDays',
  'platform.limitBookingFutureDays': 'limitBookingFutureDays',
  'platform.gymCheckIn': 'gymCheckIn',
  'platform.openGymServiceId': 'openGymService',
  'platform.allowCheckIn': 'allowCheckIn',
  'platform.checkInPayment': 'checkInPayment',
  'platform.checkInServices': 'checkInServices',
  'platform.checkInSearch': 'checkInSearch',
  'platform.checkInAssessment': 'checkInAssessment',
  'platform.disablePlatformSchedulingTab': 'hideTab',

  // Client Settings fields (settings.clientSettings prefix)
  'platform.clientThankYouMessage': 'trainingBilling.thankYouMessage',
  'platform.clientThankYouPage': 'trainingBilling.thankYouPage',
  'platform.hideAlerts': 'alerts.hideAlerts',
  'platform.redAlerts': 'alerts.redAlerts',
  'platform.useSubtrainerAsSender': 'generalSettings.useSubtrainerAsSender',
  'whiteLabeler.hideCalendars': 'generalSettings.disableClientCalendars',
  'platform.disableNutritionMacros': 'generalSettings.disableNutritionMacros',
  'platform.disableNutritionFiles': 'generalSettings.disableNutritionFiles',
  'platform.disableNutritionMyFitnessPal':
    'generalSettings.disableNutritionMyFitnessPal',
  'platform.disableEditProfileImage': 'generalSettings.disableEditProfileImage',
  'platform.disablePlatformClientsTab': 'hideTab',

  // Role Management fields (staff.rolesManagement prefix)
  defaultChildPlatformRoles: 'defaultRoles',
  'platform.blockSitesToAddRoles': 'allowSitesAddRoles',
  'platform.blockSitesToEditRoles': 'allowSitesEditRoles',

  // Add more mappings as needed...
};

/**
 * Helper function to get nested values from objects using dot notation
 */
const getNestedValue = (obj: any, path: string): any => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

/**
 * Analyzes changes in role arrays to produce detailed change descriptions
 */
const analyzeRoleChanges = (
  oldRoles: any[],
  newRoles: any[]
): SettingChange[] => {
  const changes: SettingChange[] = [];

  // Create maps for easier comparison
  const oldRolesById = new Map(oldRoles.map((role) => [role.id, role]));
  const newRolesById = new Map(newRoles.map((role) => [role.id, role]));

  // Find added roles
  newRoles.forEach((newRole) => {
    if (!oldRolesById.has(newRole.id)) {
      changes.push({
        fieldPath: `defaultChildPlatformRoles.${newRole.id}`,
        label: `Added Role: ${newRole.name}`,
        newValue: 'Added',
        oldValue: null,
      });
    }
  });

  // Find removed roles
  oldRoles.forEach((oldRole) => {
    if (!newRolesById.has(oldRole.id)) {
      changes.push({
        fieldPath: `defaultChildPlatformRoles.${oldRole.id}`,
        label: `Removed Role: ${oldRole.name}`,
        newValue: null,
        oldValue: 'Removed',
      });
    }
  });

  // Find modified roles
  newRoles.forEach((newRole) => {
    const oldRole = oldRolesById.get(newRole.id);
    if (oldRole) {
      // Check if role name changed
      if (oldRole.name !== newRole.name) {
        changes.push({
          fieldPath: `defaultChildPlatformRoles.${newRole.id}.name`,
          label: `Role Name: ${oldRole.name} → ${newRole.name}`,
          newValue: newRole.name,
          oldValue: oldRole.name,
        });
      }

      // Check if permissions changed (simplified check)
      const oldPermsString = JSON.stringify(oldRole.permissions || {});
      const newPermsString = JSON.stringify(newRole.permissions || {});
      if (oldPermsString !== newPermsString) {
        changes.push({
          fieldPath: `defaultChildPlatformRoles.${newRole.id}.permissions`,
          label: `Role Permissions: ${newRole.name}`,
          newValue: 'Updated',
          oldValue: 'Previous',
        });
      }
    }
  });

  return changes;
};

/**
 * Recursively traverses nested objects to extract field changes
 */
const traverseNestedChanges = (
  changedFields: Record<string, any>,
  initialValues: Record<string, any>,
  t: (key: string, options?: any) => string,
  pathPrefix = ''
): SettingChange[] => {
  const changes: SettingChange[] = [];

  Object.entries(changedFields).forEach(([key, newValue]) => {
    const fullPath = pathPrefix ? `${pathPrefix}.${key}` : key;

    // Special handling for defaultChildPlatformRoles
    if (key === 'defaultChildPlatformRoles' && Array.isArray(newValue)) {
      const oldRoles = (getNestedValue(initialValues, fullPath) || []) as any[];
      const roleChanges = analyzeRoleChanges(oldRoles, newValue);
      changes.push(...roleChanges);
      return;
    }

    if (
      typeof newValue === 'object' &&
      newValue !== null &&
      !Array.isArray(newValue)
    ) {
      // Recursively handle nested objects
      const nestedChanges = traverseNestedChanges(
        newValue,
        initialValues,
        t,
        fullPath
      );
      changes.push(...nestedChanges);
    } else {
      // This is a leaf value - create a change entry
      const translationKey = fieldTranslationMap[fullPath];
      let label: string;

      if (translationKey) {
        // Use the mapped translation key directly since t() already has the correct prefix
        label = t(translationKey, { defaultValue: translationKey });
      } else {
        // Fallback: convert camelCase field name to readable format
        const fieldName = key;
        label = fieldName
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, (str) => str.toUpperCase())
          .trim();
      }

      changes.push({
        fieldPath: fullPath,
        label,
        newValue,
        oldValue: getNestedValue(initialValues, fullPath),
      });
    }
  });

  return changes;
};

/**
 * Formats changed fields into human-readable SettingChange objects for display
 * Now works with nested objects directly from Formik
 */
export const formatSettingsChanges = (
  changedFields: Record<string, any>,
  initialValues: Record<string, any>,
  t: (key: string, options?: any) => string
): SettingChange[] => {
  return traverseNestedChanges(changedFields, initialValues, t);
};

/**
 * Checks if any of the changed fields are propagatable (not excluded by PlatformSettingsCopier)
 */
export const hasPropagableChanges = (
  changedFields: Record<string, any>
): boolean => {
  // Skip non-propagatable top-level keys (UI-only fields)
  const propagatableTopLevelKeys = [
    'whiteLabeler',
    'platform',
    'defaultChildPlatformRoles', // Add roles as propagatable
  ];

  const filteredFields = Object.entries(changedFields)
    .filter(([key]) => propagatableTopLevelKeys.includes(key))
    .reduce((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {} as Record<string, any>);

  return Object.keys(filteredFields).length > 0;
};
